import Component from "@glimmer/component";
import { concat } from "@ember/helper";
import { on } from "@ember/modifier";
import { action } from "@ember/object";
import { service } from "@ember/service";
import { isBlank } from "@ember/utils";
import { isExistingIconId } from "discourse/lib/icon-library";
import { emojiUrlFor } from "discourse/lib/text";
import DButton from "discourse/ui-kit/d-button";
import { i18n } from "discourse-i18n";

export default class ReactionsReactionButton extends Component {
  @service capabilities;
  @service siteSettings;
  @service site;
  @service currentUser;

  @action
  click() {
    this.args.cancelCollapse();

    const currentUserReaction = this.args.post.current_user_reaction;
    if (!this.capabilities.touch || this.site.desktopView) {
      this.args.toggleFromButton({
        reaction: currentUserReaction
          ? currentUserReaction.id
          : this.siteSettings.discourse_reactions_reaction_for_like,
      });
    }
  }

  @action
  pointerOver(event) {
    if (event.pointerType !== "mouse") {
      return;
    }

    this.args.cancelCollapse();

    // Anonymous users haven't authenticated yet — they can still pick a
    // reaction, it gets deferred until login. Archived topics reject
    // reactions server-side; closed topics still accept them.
    if (!this.currentUser) {
      if (this.args.post.topic?.archived) {
        return;
      }
      this.args.toggleReactions(event);
      return;
    }

    const likeAction = this.args.post.likeAction;
    if (!likeAction?.canToggle) {
      return;
    }

    const currentUserReaction = this.args.post.current_user_reaction;
    if (
      currentUserReaction &&
      !currentUserReaction.can_undo &&
      isBlank(likeAction.can_undo)
    ) {
      return;
    }

    this.args.toggleReactions(event);
  }

  @action
  pointerOut(event) {
    if (event.pointerType !== "mouse") {
      return;
    }

    this.args.scheduleCollapse("collapseReactionsPicker");
  }

  get likedIcon() {
    const icon = this.siteSettings.discourse_reactions_like_icon;
    // Map "heart" to the d-liked alias to follow core replacement pattern
    if (icon === "heart") {
      return "d-liked";
    }

    return icon;
  }

  get unlikedIcon() {
    const icon = this.siteSettings.discourse_reactions_like_icon;
    // Map "heart" to the d-unliked alias to follow core replacement pattern
    if (icon === "heart") {
      return "d-unliked";
    }

    // Not all icons have a far- version, so we need to check if it exists.
    if (isExistingIconId(`far-${icon}`)) {
      return `far-${icon}`;
    }

    return icon;
  }

  get title() {
    if (!this.currentUser) {
      return i18n("discourse_reactions.main_reaction.unauthenticated");
    }

    const likeAction = this.args.post.likeAction;
    if (!likeAction) {
      return null;
    }

    let title;
    let options;
    const currentUserReaction = this.args.post.current_user_reaction;

    if (likeAction.canToggle && isBlank(likeAction.can_undo)) {
      title = "discourse_reactions.main_reaction.add";
    }

    if (likeAction.canToggle && likeAction.can_undo) {
      title = "discourse_reactions.main_reaction.remove";
    }

    if (!likeAction.canToggle) {
      title = "discourse_reactions.main_reaction.cant_remove";
    }

    if (
      currentUserReaction &&
      currentUserReaction.can_undo &&
      isBlank(likeAction.can_undo)
    ) {
      title = "discourse_reactions.picker.remove_reaction";
      options = { reaction: currentUserReaction.id };
    }

    if (
      currentUserReaction &&
      !currentUserReaction.can_undo &&
      isBlank(likeAction.can_undo)
    ) {
      title = "discourse_reactions.picker.cant_remove_reaction";
    }

    return options ? i18n(title, options) : i18n(title);
  }

  <template>
    {{! eslint-disable ember/template-no-invalid-interactive }}
    <div
      class="discourse-reactions-reaction-button"
      {{on "click" this.click}}
      {{on "pointerover" this.pointerOver}}
      {{on "pointerout" this.pointerOut}}
      title={{this.title}}
    >
      {{#if @post.current_user_used_main_reaction}}
        <DButton
          class="btn-toggle-reaction-like btn-flat btn-icon no-text reaction-button"
          @translatedTitle={{this.title}}
          @icon={{this.likedIcon}}
        />
      {{else if @post.current_user_reaction}}
        <DButton
          class="btn-icon no-text btn-flat reaction-button"
          @translatedTitle={{this.title}}
        >
          <img
            class="btn-toggle-reaction-emoji reaction-button"
            src={{emojiUrlFor @post.current_user_reaction.id}}
            alt={{concat ":" @post.current_user_reaction.id}}
          />
        </DButton>
      {{else}}
        <DButton
          class="btn-toggle-reaction-like btn-flat btn-icon no-text reaction-button"
          @translatedTitle={{this.title}}
          @icon={{this.unlikedIcon}}
        />
      {{/if}}
    </div>
  </template>
}
